import { c as _c } from "react/compiler-runtime";
import { z } from "zod";
import { useTranslations } from "next-intl";
import { ValidatedInput, ValidatedTextarea, ValidatedSelect } from "@/components/form/validated-field";
import { VenueTypeItemEnum } from "../../types";
const field = {
  firstName: {
    key: "firstName" as const,
    schema: z.string().min(1, "First name is required"),
    defaultValue: ""
  },
  lastName: {
    key: "lastName" as const,
    schema: z.string().min(1, "Last name is required"),
    defaultValue: ""
  },
  email: {
    key: "email" as const,
    schema: z.string().email("Please enter a valid email address"),
    defaultValue: ""
  },
  company: {
    key: "company" as const,
    schema: z.string().optional(),
    defaultValue: ""
  },
  venueType: {
    key: "venueType" as const,
    schema: z.string().optional(),
    defaultValue: ""
  },
  comments: {
    key: "comments" as const,
    schema: z.string().optional(),
    defaultValue: ""
  }
};
export const demoRequestFormSchema = z.object({
  firstName: field.firstName.schema,
  lastName: field.lastName.schema,
  email: field.email.schema,
  company: field.company.schema,
  venueType: field.venueType.schema,
  comments: field.comments.schema
});
export type DemoRequestFormData = z.infer<typeof demoRequestFormSchema>;
type FieldProps = Readonly<{
  disabled?: boolean;
}>;
function FirstName(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("welcome.demoRequestForm");
  let t1;
  if ($[0] !== t) {
    t1 = t("firstNameLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("firstNamePlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.firstName.key} id={field.firstName.key} className="w-full" placeholder={t2} required={true} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function LastName(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("welcome.demoRequestForm");
  let t1;
  if ($[0] !== t) {
    t1 = t("lastNameLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("lastNamePlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.lastName.key} id={field.lastName.key} className="w-full" placeholder={t2} required={true} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function Email(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("welcome.demoRequestForm");
  let t1;
  if ($[0] !== t) {
    t1 = t("emailLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("emailPlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.email.key} id={field.email.key} type="email" className="w-full" placeholder={t2} required={true} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function Company(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("welcome.demoRequestForm");
  let t1;
  if ($[0] !== t) {
    t1 = t("companyLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("companyPlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.company.key} id={field.company.key} className="w-full" placeholder={t2} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function VenueType(t0) {
  const $ = _c(12);
  const {
    disabled
  } = t0;
  const t = useTranslations("welcome.demoRequestForm");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Object.values(VenueTypeItemEnum);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t1.map(value => ({
      value,
      label: t(`venueTypes.${value}`)
    }));
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const venueTypeOptions = t2;
  let t3;
  if ($[3] !== t) {
    t3 = t("venueTypeLabel");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("venueTypePlaceholder");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== disabled || $[8] !== t3 || $[9] !== t4 || $[10] !== venueTypeOptions) {
    t5 = <ValidatedSelect label={t3} name={field.venueType.key} className="w-full" placeholder={t4} options={venueTypeOptions} disabled={disabled} />;
    $[7] = disabled;
    $[8] = t3;
    $[9] = t4;
    $[10] = venueTypeOptions;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function Comments(t0) {
  const $ = _c(5);
  const {
    disabled
  } = t0;
  const t = useTranslations("welcome.demoRequestForm");
  let t1;
  if ($[0] !== t) {
    t1 = t("commentsLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== disabled || $[3] !== t1) {
    t2 = <ValidatedTextarea name={field.comments.key} label={t1} disabled={disabled} />;
    $[2] = disabled;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export const FormField = {
  FirstName,
  LastName,
  Email,
  Company,
  VenueType,
  Comments
};